/**
 * @fileoverview Audio Coordination Middleware
 *
 * Enforces single audio stream playback across the entire application.
 * Automatically stops any currently playing audio when new audio starts.
 *
 * **How it works:**
 * 1. Listens for playAudio and loadAudio actions
 * 2. If audio is currently playing, dispatches stopAudio
 * 3. Allows the new playAudio action to proceed
 *
 * This middleware ensures only ONE audio stream is ever playing,
 * regardless of player type (WaveSurfer, Howler, native audio).
 *
 * @module store/middleware/audioCoordinationMiddleware
 */

import type { Middleware } from '@reduxjs/toolkit'
import { isAnyOf } from '@reduxjs/toolkit'
import { playAudio, loadAudio, stopAudio } from '../slices/globalAudioSlice'

export const audioCoordinationMiddleware: Middleware =
  (store) => (next) => (action) => {
    // Check if this is a play or load action
    if (isAnyOf(playAudio, loadAudio)(action)) {
      const state = store.getState()
      const currentAudioId = state.globalAudio.currentAudioId
      const newAudioId = action.payload.audioId

      // If different audio is currently active, stop it first
      if (currentAudioId && currentAudioId !== newAudioId) {
        // Dispatch stopAudio before allowing the new play/load action
        store.dispatch(stopAudio())
      }
    }

    // Continue with the action
    return next(action)
  }
